import { useCallback, useEffect, useRef } from "react";

/**
 * Two or more strips that scroll as one.
 *
 * The screen card draws content and campaign lanes as separate scroll
 * containers - they have to be, because each carries its own pinned rail. But
 * they are one picture of one day: the whole point of stacking them is that
 * "which campaign is eligible while THAT playlist is on" is a vertical line.
 * Scrolled apart, the columns stop standing under the same hours and the line
 * lies.
 *
 * ## Why a shared ref rather than state
 *
 * Scroll position is not something React should re-render on. A `useState`
 * here would repaint both strips on every scroll frame - on a page showing
 * twenty screens, that is twenty cards re-rendering while somebody drags a
 * scrollbar. The elements are written to directly.
 *
 * ## The echo, and why it does not loop
 *
 * Setting `scrollLeft` on the follower fires its OWN scroll event, which would
 * write back to the leader, which fires again. The flag below makes the
 * propagation one-way for the duration of a sync, so an echo is ignored rather
 * than answered. Comparing values alone very nearly works and jitters at
 * fractional offsets on trackpads, which is exactly the kind of bug that only
 * appears on somebody else's hardware.
 */
export interface LinkedScroll {
  /** Ref callback. Pass to every element that should move together. */
  register: (el: HTMLElement | null) => void;
}

export function useLinkedScroll(): LinkedScroll {
  const elements = useRef(new Set<HTMLElement>());
  const syncing = useRef(false);

  const onScroll = useCallback((event: Event) => {
    if (syncing.current) return;
    const source = event.currentTarget as HTMLElement;
    syncing.current = true;
    try {
      for (const el of elements.current) {
        if (el === source) continue;
        // Only when it actually differs: an assignment that changes nothing
        // still fires a scroll event on some engines.
        if (el.scrollLeft !== source.scrollLeft) {
          el.scrollLeft = source.scrollLeft;
        }
      }
    } finally {
      // Cleared on the next frame, not synchronously: the scroll events the
      // writes above provoke are delivered asynchronously, and clearing here
      // would let the first echo through.
      requestAnimationFrame(() => {
        syncing.current = false;
      });
    }
  }, []);

  const register = useCallback(
    (el: HTMLElement | null) => {
      if (!el) return;
      if (elements.current.has(el)) return;
      elements.current.add(el);
      el.addEventListener("scroll", onScroll, { passive: true });
    },
    [onScroll]
  );

  useEffect(() => {
    const live = elements.current;
    return () => {
      for (const el of live) el.removeEventListener("scroll", onScroll);
      live.clear();
    };
  }, [onScroll]);

  return { register };
}
